/* Keep this romper's customer choices independent until a complete variant exists. */
(() => {
  async function init() {
    const root = document.getElementById('GGAddonLive');
    const configNode = root?.closest('[id^="ProductInfo-"]')?.querySelector('[data-gg-romper-options]');
    if (!root || !configNode || root.dataset.ready) return;
    root.dataset.ready = 'true';
    await customElements.whenDefined('variant-selects');

    const info = root.closest('[id^="ProductInfo-"]');
    const picker = info.querySelector('variant-selects');
    if (!picker) return;
    const section = picker.getParent();
    const config = JSON.parse(configNode.textContent);
    const variants = config.variants;
    const fields = () => Array.from(picker.querySelectorAll('fieldset'));
    const boxes = Array.from(root.querySelectorAll('input[type="checkbox"]'));
    const status = info.querySelector('.gg-romper-status');
    const form = section.querySelector(`#product-form-${picker.dataset.section}`);
    const money = new Intl.NumberFormat(config.locale || 'en', {
      style: 'currency', currency: config.currency || 'USD'
    });
    const initial = config.selectedVariant;
    const selection = initial ? [...initial.options] : [null, 'No add-ons', null];
    let version = 0;
    let request;
    let committedId = null;
    let loading = false;
    let failed = false;

    // This controller keeps incomplete choices itself; the vendor's initial
    // prompt would otherwise hide the selected size and the disabled cart button.
    section.querySelector('select-option-js')?.remove();
    section.querySelector('.select-options-button')?.remove();
    fields()[2]?.before(root);
    root.append(status);
    const groupNames = fields().map(field => field.querySelector('input[type="radio"]')?.name);
    const selectedVariant = () => variants.find(variant =>
      variant.options.every((value, index) => value === selection[index])
    );
    const idInputs = () => section.querySelectorAll(
      `#product-form-${picker.dataset.section} input[name="id"], ` +
      `#product-form-installment-${picker.dataset.section} input[name="id"]`
    );

    function render() {
      fields().forEach((field, index) => {
        field.querySelectorAll('input[type="radio"]').forEach(input => {
          input.checked = input.value === selection[index];
          if (index !== 2) return;
          const label = field.querySelector(`label[for="${CSS.escape(input.id)}"]`);
          if (!label || label.querySelector('.gg-style-preview')) return;
          const variant = variants.find(item => item.option3 === input.value);
          const src = variant?.featured_image?.src;
          if (!src) return;
          const image = document.createElement('img');
          image.className = 'gg-style-preview';
          image.alt = input.value;
          image.width = image.height = 110;
          image.src = src;
          label.prepend(image);
        });
        const label = field.querySelector('.variants-label__value');
        if (label) label.textContent = selection[index] || (index === 0 ? 'Choose size' : 'Choose style');
      });
      boxes.forEach(box => { box.checked = selection[1].includes(box.value); });
      root.querySelector('.gg-addon-total span').textContent = selection[1];
      const count = boxes.filter(box => box.checked).length;
      root.querySelector('.gg-addon-total strong').textContent = '+' + money.format(count * 5);

      // Show a real matching price even while the required choices are incomplete.
      const candidates = variants.filter(variant => variant.options.every((value, index) =>
        !selection[index] || value === selection[index]
      ));
      const prices = candidates.map(variant => variant.price);
      const price = info.querySelector('[id^="price-"] .price__main');
      if (price && prices.length) {
        const min = Math.min(...prices);
        price.textContent = (min !== Math.max(...prices) ? 'From ' : '') + money.format(min / 100);
      }
      const variant = selectedVariant();
      const ready = !loading && !failed && variant?.available && committedId === variant.id;
      picker.toggleAddButton(!ready);
      const button = form?.querySelector('[name="add"]');
      if (button) {
        const incomplete = !selection[0] || !selection[2];
        const label = loading ? 'Updating your selection…' : incomplete || failed
          ? 'Select the options above' : !variant?.available ? 'Sold out' : 'Add to Cart';
        button.dataset.ggPurchasePending = String(!ready);
        button.setAttribute('aria-busy', String(loading));
        button.querySelectorAll('.btn__texts__item').forEach(item => { item.textContent = label; });
      }
      idInputs().forEach(input => { input.disabled = !ready; });
      picker.setAttribute('aria-busy', String(loading));
      status.textContent = loading ? 'Updating your selection…' : failed
        ? 'Could not update your selection. Please select an option again to retry.'
        : !selection[0] || !selection[2] ? 'Please choose your size and style. Add-ons are optional.'
        : !variant?.available ? 'This combination is unavailable. Please choose another option.' : '';
    }

    function invalidateURL() {
      if (picker.dataset.updateUrl === 'false') return;
      const url = new URL(window.location.href);
      url.searchParams.delete('variant');
      window.history.replaceState({}, '', url);
    }

    async function update() {
      const currentVersion = ++version;
      request?.abort();
      const variant = selectedVariant();
      failed = false;
      loading = false;
      committedId = null;
      render();
      if (!variant) { invalidateURL(); return; }
      loading = true;
      render();
      request = new AbortController();
      const url = new URL(picker.dataset.url, window.location.origin);
      url.searchParams.set('variant', variant.id);
      url.searchParams.set('section_id', picker.dataset.originalSection || picker.dataset.section);
      // Preserve alternate views and the explicit preview target on every section request.
      const currentURL = new URL(window.location.href);
      for (const key of ['view', 'preview_theme_id']) {
        if (currentURL.searchParams.has(key)) url.searchParams.set(key, currentURL.searchParams.get(key));
      }
      try {
        const response = await fetch(url, { signal: request.signal });
        if (!response.ok) throw new Error('Section request failed');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (currentVersion !== version) return;
        const json = html.querySelector('variant-selects script[type="application/json"]')?.textContent;
        if (!json || JSON.parse(json).id !== variant.id) throw new Error('Variant response mismatch');

        picker.variant_id = String(variant.id);
        picker.new_html = html;
        picker.newJSON = json;
        picker.querySelector('script[type="application/json"]').textContent = json;
        picker.updateMasterId();
        picker.updateOptions();
        picker.updatePickupAvailability();
        picker.removeErrorMessage();
        picker.updateVariantStatuses();
        picker.updateMedia();
        picker.updateVariantInput();
        picker.renderProductInfo();
        // The native URL method drops preview/view parameters, so retain them here.
        if (picker.dataset.updateUrl !== 'false') {
          const nextURL = new URL(window.location.href);
          nextURL.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', nextURL);
        }
        picker.updateShareUrl();
        committedId = variant.id;
      } catch (error) {
        if (currentVersion !== version || error.name === 'AbortError') return;
        failed = true;
        invalidateURL();
      } finally {
        if (currentVersion === version) { loading = false; render(); }
      }
    }

    // Capture before the vendor picker can resolve a single option to a whole variant.
    picker.addEventListener('change', event => {
      const input = event.target;
      if (boxes.includes(input)) {
        event.stopImmediatePropagation();
        const chosen = boxes.filter(box => box.checked).map(box => box.value);
        selection[1] = chosen.length === 2 ? 'Red Booties + Red Headband' : chosen[0] || 'No add-ons';
      } else if (input.matches('input.product-form__item__input')) {
        const index = groupNames.indexOf(input.name);
        if (index < 0) return;
        event.stopImmediatePropagation();
        selection[index] = input.value;
      } else return;
      update();
    }, true);

    form?.addEventListener('submit', event => {
      const variant = selectedVariant();
      if (loading || failed || !variant?.available || committedId !== variant.id) {
        event.preventDefault();
        event.stopImmediatePropagation();
        render();
      }
    }, true);
    update();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();
